import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useClients } from '../context/ClientsContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

function clientCoreBaseUrl() {
  return String(process.env.REACT_APP_CLIENT_CORE_API_URL || '').replace(/\/+$/, '');
}

function companyRequestHeaders(token, user, companyId) {
  const headers = buildConsoleRequestHeaders(token, user);
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator') && companyId) {
    headers['X-Company-Id'] = String(companyId);
  }
  return headers;
}

function resolveCompanyRecord(user, clients, companyId) {
  const targetId = String(companyId || '').trim();
  if (!targetId) {
    return null;
  }

  const candidates = [user?.activeCompany, user?.myCompany, user?.company].filter(Boolean);
  for (const company of candidates) {
    const id = String(company.id ?? company.company_id ?? company.companyId ?? '').trim();
    if (id === targetId) {
      return company;
    }
  }

  const list = Array.isArray(clients) ? clients : [];
  return list.find((company) => String(company?.id ?? '') === targetId) || null;
}

export function extractWidgetConfig(company) {
  const settings = company?.settings;
  if (!settings || typeof settings !== 'object') {
    return null;
  }
  const widgetConfig = settings.widgetConfig ?? settings.widget_config;
  if (!widgetConfig || typeof widgetConfig !== 'object') {
    return null;
  }
  return widgetConfig;
}

function pickConfigValue(source, ...keys) {
  if (!source || typeof source !== 'object') {
    return undefined;
  }
  for (const key of keys) {
    const value = source[key];
    if (value == null) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    return value;
  }
  return undefined;
}

export function normalizeWidgetConfig(raw) {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const normalized = {
    apiBaseUrl: pickConfigValue(raw, 'apiBaseUrl', 'api_base_url'),
    deliveryCoreUrl: pickConfigValue(raw, 'deliveryCoreUrl', 'delivery_core_url'),
    authIdentityUrl: pickConfigValue(raw, 'authIdentityUrl', 'auth_identity_url'),
    clientCoreUrl: pickConfigValue(raw, 'clientCoreUrl', 'client_core_url'),
    apiKey: pickConfigValue(raw, 'apiKey', 'api_key'),
    googleMapsApiKey: pickConfigValue(raw, 'googleMapsApiKey', 'google_maps_api_key'),
    allowExternalPhoneNumber: pickConfigValue(
      raw,
      'allowExternalPhoneNumber',
      'allow_external_phone_number'
    ),
    maxWidth: pickConfigValue(raw, 'maxWidth', 'max_width'),
    themeColor: pickConfigValue(raw, 'themeColor', 'theme_color'),
    refreshIntervalMinutes: pickConfigValue(
      raw,
      'refreshIntervalMinutes',
      'refresh_interval_minutes'
    ),
    defaultDeliveryType: pickConfigValue(raw, 'defaultDeliveryType', 'default_delivery_type'),
    environment: pickConfigValue(raw, 'environment'),
    locale: pickConfigValue(raw, 'locale'),
    companyId: pickConfigValue(raw, 'companyId', 'company_id'),
    customerId: pickConfigValue(raw, 'customerId', 'customer_id'),
  };

  return Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => value !== undefined)
  );
}

export function useCompanyWidgetConfig(companyId) {
  const { user } = useAuth();
  const { clients } = useClients();
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWidgetConfig = useCallback(async () => {
    const targetId = String(companyId || '').trim();
    if (!targetId) {
      setWidgetConfig(null);
      setError('ordersCreateWidgetMissingCompany');
      return;
    }

    const cachedCompany = resolveCompanyRecord(user, clients, targetId);
    const cachedWidgetConfig = extractWidgetConfig(cachedCompany);
    if (cachedWidgetConfig) {
      setWidgetConfig(normalizeWidgetConfig(cachedWidgetConfig));
      setError(null);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setWidgetConfig(null);
      setError('ordersMissingToken');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = `${clientCoreBaseUrl()}/v1/clients/company`;
      const res = await fetch(url, {
        headers: companyRequestHeaders(token, user, targetId),
      });
      if (!res.ok) {
        setWidgetConfig(null);
        setError('ordersCreateWidgetConfigLoadFailed');
        return;
      }
      const company = await res.json();
      const loadedWidgetConfig = extractWidgetConfig(company);
      if (!loadedWidgetConfig) {
        setWidgetConfig(null);
        setError('ordersCreateWidgetConfigMissing');
        return;
      }
      setWidgetConfig(normalizeWidgetConfig(loadedWidgetConfig));
    } catch (err) {
      console.error('Failed to load company widget config:', err);
      setWidgetConfig(null);
      setError('ordersCreateWidgetConfigLoadFailed');
    } finally {
      setLoading(false);
    }
  }, [companyId, user, clients]);

  useEffect(() => {
    loadWidgetConfig();
  }, [loadWidgetConfig]);

  return { widgetConfig, loading, error };
}
